/**
 * quizApi — thin JSON client for Reader::QuizzesController (TASK-015 Phase
 * 2, normalized Quiz/QuizQuestion + free navigation). Every mutation/grading
 * decision lives on the server; this module only shuttles requests/responses.
 *
 * Wire format note (task doc, "Contratto API"): `answers` is indexed by
 * **position** (the question's index within `questions[]`, per Phase 1's
 * `QuizPresenter.focus_questions` ordering), never by `question_id` — the
 * position is the question's identity inside a quiz, and doesn't depend on
 * the pool's content-hash fingerprint. Callers (QuizSession.tsx) keep their
 * own state keyed by `question_id` and convert to position at this boundary.
 *
 * Deliberately NOT exposed here (matches the server's own invariants):
 *   - no bulk "list all questions" call — the pool never leaves the server.
 *   - no way to read `correct_option_idx`/`rationale` for any question before
 *     the quiz is completed (invariant 1) — `saveAnswers` only ever echoes
 *     `{ ok: true }`, `completeQuiz` is the first response allowed to expose
 *     a score, and even that is a derived aggregate, never a per-question
 *     solution.
 */

import { getCsrfToken } from './syncApi.js'

export type QuizMode = 'coverage' | 'fragile' | 'chapter'

export interface QuizSessionState {
  quiz_session_id: string
  num_questions: number
  current_idx: number
  completed_at: string | null
  score_json: { correct: number; wrong: number; skipped: number; total: number } | null
}

export interface QuizScoreResult {
  correct: number
  wrong: number
  skipped: number
  total: number
}

export interface QuizCompleteResult {
  completed_at: string
  score: QuizScoreResult
}

/** Position-keyed answers map: array index within `questions[]` → selected option, or `null` for skipped. */
export type QuizAnswersByPosition = Record<number, number | null>

function jsonHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  const data = (await res.json().catch(() => null)) as (T & { error?: string }) | null
  if (!res.ok) {
    throw new Error(data?.error ?? `Richiesta quiz fallita: ${res.status}`)
  }
  return data as T
}

export async function createQuizSession(
  versionId: number,
  mode: QuizMode,
  focusChapterId?: string,
): Promise<string> {
  const res = await fetch(`/read/${versionId}/quiz/sessions`, {
    method: 'POST',
    headers: jsonHeaders(),
    credentials: 'same-origin',
    body: JSON.stringify({
      mode,
      ...(focusChapterId != null ? { focus_chapter_id: focusChapterId } : {}),
    }),
  })
  const data = await parseJsonOrThrow<{ quiz_session_id: string }>(res)
  return data.quiz_session_id
}

export async function fetchQuizSessionState(
  versionId: number,
  sessionId: string,
): Promise<QuizSessionState> {
  const res = await fetch(`/read/${versionId}/quiz/sessions/${sessionId}`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
  return parseJsonOrThrow<QuizSessionState>(res)
}

/**
 * Autosave — merges the given position → selected_option_idx entries into
 * the quiz (server-side semantics: merge by position, re-sending the same
 * payload is a no-op — task doc "Idempotenza"). `currentIdx` is a pure
 * bookmark (`quizzes.current_idx`); no business logic reads it.
 *
 * Callers are expected to treat failures as fire-and-forget: on rejection,
 * the caller should keep the local selection intact and simply retry on the
 * next change, never block the UI or roll back state (task brief).
 */
export async function saveAnswers(
  versionId: number,
  sessionId: string,
  answers: QuizAnswersByPosition,
  currentIdx?: number,
): Promise<void> {
  const res = await fetch(`/read/${versionId}/quiz/sessions/${sessionId}/answers`, {
    method: 'PATCH',
    headers: jsonHeaders(),
    credentials: 'same-origin',
    body: JSON.stringify({
      answers,
      ...(currentIdx != null ? { current_idx: currentIdx } : {}),
    }),
  })
  await parseJsonOrThrow<{ ok: true }>(res)
}

/**
 * Finalizes the quiz — flushes the full answers map (self-healing against
 * any autosave that hasn't landed yet) and asks the server to derive+store
 * the score. Idempotent: calling this twice on an already-completed quiz
 * returns the same `completed_at`/`score` without rewriting anything.
 */
export async function completeQuiz(
  versionId: number,
  sessionId: string,
  answers: QuizAnswersByPosition,
): Promise<QuizCompleteResult> {
  const res = await fetch(`/read/${versionId}/quiz/sessions/${sessionId}/complete`, {
    method: 'POST',
    headers: jsonHeaders(),
    credentials: 'same-origin',
    body: JSON.stringify({ answers }),
  })
  return parseJsonOrThrow<QuizCompleteResult>(res)
}

/**
 * Discards an in-progress quiz. `409` (server: quiz already completed, so
 * immutable — invariant 5) surfaces as a thrown Error with a message the
 * caller can show directly; any other non-2xx does the same.
 */
export async function deleteQuiz(versionId: number, sessionId: string): Promise<void> {
  const res = await fetch(`/read/${versionId}/quiz/sessions/${sessionId}`, {
    method: 'DELETE',
    headers: jsonHeaders(),
    credentials: 'same-origin',
  })
  if (res.ok) return
  const data = (await res.json().catch(() => null)) as { error?: string } | null
  if (res.status === 409) {
    throw new Error(data?.error ?? 'Il quiz è già stato completato: non può essere scartato.')
  }
  throw new Error(data?.error ?? `Richiesta quiz fallita: ${res.status}`)
}
